import * as React from 'react'

import { classList } from 'react-classlist-helper'

import Localization from '~/client/src/shared/localization/LocalizationManager'

import {
  CardList,
  CardListEmpty,
  CardListSummaryBar,
  CardListLoading,
  useCardListCore,
} from '../CardList'
import { buildResourceCardModel } from './resourceCardMapper'
import { IResourceCardListProps } from './types'

import './ResourceCardList.scss'

const ResourceCardList: React.FC<IResourceCardListProps> = ({
  resourceTypeId,
  records,
  cardConfiguration,
  properties,
  getDisplayValue,
  resolveMetaItem,
  resolveSubtitle,
  titleProperty = 'name',
  loading = false,
  emptyMessage,
  className,
  headerRight,
  countLabel,
  groupConfig,
  renderGroupHeader,
  children,
}) => {
  const listState = useCardListCore({
    items: records,
    groupConfig,
  })

  // A band gets a top divider only when rows of the group above sit right over it.
  const headersWithTopBorder = React.useMemo(() => {
    const groups = listState.visibleGroups ?? listState.groups
    const keys = new Set<string>()
    groups.forEach((group, index) => {
      const previous = groups[index - 1]
      if (previous && !previous.isCollapsed && previous.items.length > 0) {
        keys.add(group.key)
      }
    })
    return keys
  }, [listState.visibleGroups, listState.groups])

  const header = (
    <CardListSummaryBar
      listState={groupConfig ? listState : undefined}
      trailing={
        headerRight && (
          <div className="tw:ml-auto tw:shrink-0">{headerRight}</div>
        )
      }
    >
      {countLabel
        ? countLabel(records.length)
        : Localization.translator.xItemsCount(records.length)}
    </CardListSummaryBar>
  )

  return (
    <CardList
      items={records}
      listState={groupConfig ? listState : undefined}
      loading={loading}
      empty={
        <CardListEmpty
          message={emptyMessage || Localization.translator.noDataAvailable}
        />
      }
      loadingComponent={<CardListLoading />}
      className={className || ''}
      header={header}
      renderGroupHeader={
        renderGroupHeader
          ? (group, onToggle) => (
              // Also keeps CardList's last-row `[&>*]:border-b-0` off the band itself.
              <div
                className={classList({
                  'tw:border-0 tw:border-t tw:border-solid tw:border-input-soft':
                    headersWithTopBorder.has(group.key),
                })}
              >
                {renderGroupHeader(group, onToggle)}
              </div>
            )
          : undefined
      }
      renderItem={(record, _depth, position) => {
        const model = buildResourceCardModel({
          record,
          resourceTypeId,
          cardConfiguration,
          properties,
          titleProperty,
          getDisplayValue,
          resolveMetaItem,
          resolveSubtitle,
        })

        return (
          <div
            className={classList({
              'tw:px-2.5 tw:py-px': true,
              'tw:pt-2.5': !!position?.isFirst,
              'tw:pb-2.5': !!position?.isLast,
            })}
          >
            {children(record, model)}
          </div>
        )
      }}
    />
  )
}

export default ResourceCardList
